import { useEffect, useState, useCallback } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Trash2, Power, PlusCircle, Eye, Pencil, MinusCircle } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, apiError } from "@/lib/api";
import { rate, rupee, stockUnit, saleRate, STATUS_LABELS, statusColor } from "@/lib/format";
import ListToolbar from "@/components/ListToolbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useAddProduct } from "@/lib/useAddProduct";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function MyProducts() {
  const { access, sub } = useOutletContext();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ status: "", size: "", surface: "" });
  const [sort, setSort] = useState("newest");
  const [reduceP, setReduceP] = useState(null);
  const disabled = access !== "full";
  const addProduct = useAddProduct(access, sub?.slots);
  const [options, setOptions] = useState({ sizes: [], surfaces: [] });
  useEffect(() => { api.get("/product-options").then(({ data }) => setOptions(data)); }, []);

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (search) p.set("q", search);
    if (filters.status) p.set("status", filters.status);
    if (filters.size) p.set("size", filters.size);
    if (filters.surface) p.set("surface", filters.surface);
    p.set("sort", sort);
    const { data } = await api.get(`/products?${p.toString()}`);
    setItems(data);
  }, [search, filters, sort]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const act = async (fn) => { try { await fn(); toast.success("Done"); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold">My Products</h1>
          <p className="text-muted-foreground text-sm font-medium">Manage your tile stock listings.</p>
        </div>
        <Button data-testid="add-product-btn" onClick={addProduct} disabled={access === "view_only"} className="font-bold gap-2"><PlusCircle className="h-4 w-4" /> Add Product</Button>
      </div>

      {disabled && <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-2 text-sm font-semibold text-amber-800">Management is disabled in your current subscription state — view only.</div>}

      <ListToolbar
        placeholder="Search product, size, surface…"
        search={search} setSearch={setSearch}
        filters={[
          { key: "status", label: "Status", value: filters.status, options: Object.entries(STATUS_LABELS).map(([v, l]) => ({ value: v, label: l })) },
          { key: "size", label: "Size", value: filters.size, options: options.sizes.map((s) => ({ value: s, label: s })) },
          { key: "surface", label: "Surface", value: filters.surface, options: options.surfaces.map((s) => ({ value: s, label: s })) },
        ]}
        onFilter={(k, v) => setFilters((f) => ({ ...f, [k]: v }))}
        sort={sort} setSort={setSort}
        sorts={[{ value: "newest", label: "Newest" }, { value: "oldest", label: "Oldest" }, { value: "price_low", label: "Price ↑" }, { value: "price_high", label: "Price ↓" }, { value: "most_viewed", label: "Most Viewed" }]}
        onReset={() => { setSearch(""); setFilters({ status: "", size: "", surface: "" }); setSort("newest"); }}
      />

      {items.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground font-semibold">No products yet.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <div key={p.id} data-testid={`myproduct-${p.id}`} className="bg-card border rounded-xl overflow-hidden">
              <div className="relative aspect-video bg-secondary">
                <img src={fileUrl(p.catalogue_image)} alt={p.title} className="h-full w-full object-cover" />
                <Badge className={`absolute top-2 left-2 border ${statusColor(p.status)}`}>{STATUS_LABELS[p.status]}</Badge>
              </div>
              <div className="p-3">
                <h3 className="font-heading font-bold text-sm line-clamp-1">{p.title}</h3>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-primary font-extrabold">{rate(p.rate_value, p.rate_unit)}</span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1"><Eye className="h-3 w-3" />{p.views || 0}</span>
                </div>
                <div className="text-xs text-muted-foreground font-medium mt-0.5">{p.size} · {p.surface} · {p.city}</div>
                <div className="text-xs font-semibold mt-1" data-testid={`stock-${p.id}`}>Stock: <span className={p.stock_qty > 0 ? "text-foreground" : "text-rose-600"}>{p.stock_qty ?? 0} {stockUnit(p)}</span></div>
                <div className="grid grid-cols-2 gap-1 mt-3">
                  <Button size="sm" variant="outline" disabled={disabled} onClick={() => nav(`/seller/add-product?edit=${p.id}`)} data-testid={`edit-${p.id}`} className="font-semibold gap-1"><Pencil className="h-3.5 w-3.5" /> Edit</Button>
                  <Button size="sm" variant="outline" disabled={disabled || (p.stock_qty ?? 0) <= 0} onClick={() => setReduceP(p)} data-testid={`reduce-${p.id}`} className="font-semibold gap-1"><MinusCircle className="h-3.5 w-3.5 text-amber-600" /> Reduce</Button>
                </div>
                <div className="flex gap-1 mt-1">
                  {p.status === "active" ? (
                    <Button size="sm" variant="outline" className="flex-1" disabled={disabled} onClick={() => act(() => api.patch(`/products/${p.id}/status?status=inactive`))} data-testid={`deactivate-${p.id}`}><Power className="h-3.5 w-3.5" /></Button>
                  ) : p.status === "inactive" ? (
                    <Button size="sm" variant="outline" className="flex-1" disabled={disabled} onClick={() => act(() => api.patch(`/products/${p.id}/status?status=active`))} data-testid={`activate-${p.id}`}><Power className="h-3.5 w-3.5 text-emerald-600" /></Button>
                  ) : null}
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button size="sm" variant="outline" className="flex-1" disabled={disabled} data-testid={`delete-${p.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Delete this product?</AlertDialogTitle><AlertDialogDescription>This frees up its product slot. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => act(() => api.delete(`/products/${p.id}`))}>Delete</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <ReduceStockDialog product={reduceP} onClose={() => setReduceP(null)} onDone={load} />
    </div>
  );
}

function ReduceStockDialog({ product, onClose, onDone }) {
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setQty(""); }, [product]);
  if (!product) return null;
  const prev = product.stock_qty ?? 0;
  const sr = saleRate(product);
  const q = Number(qty) || 0;
  const newStock = Math.max(0, prev - q);
  const value = q * sr.value;
  const invalid = q <= 0 || q > prev;

  const submit = async () => {
    if (invalid) { toast.error(`Enter a quantity between 1 and ${prev}`); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/products/${product.id}/reduce-stock`, { qty_sold: q });
      toast.success(`${data.qty_sold} ${stockUnit(product)} marked sold · ${rupee(data.value)} added to cleared value${data.sold_out ? " · listing now Sold Out" : ""}`);
      onDone();
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={!!product} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="reduce-stock-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Reduce Inventory</DialogTitle>
          <DialogDescription className="text-xs">Enter how many {stockUnit(product).toLowerCase()} you have sold. This stock is deducted and added to your cleared sales value.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm font-semibold">{product.title}</div>
          <div className="flex items-center justify-between text-sm bg-secondary/40 rounded-lg px-3 py-2">
            <span className="text-muted-foreground font-medium">Current stock</span>
            <span className="font-bold">{prev} {stockUnit(product)}</span>
          </div>
          <div>
            <label className="text-sm font-bold">Quantity sold ({stockUnit(product)})</label>
            <Input data-testid="reduce-qty-input" type="number" min="1" max={prev} value={qty} onChange={(e) => setQty(e.target.value)} placeholder={`1 – ${prev}`} className="h-11" autoFocus />
          </div>
          {q > 0 && (
            <div data-testid="reduce-preview" className="rounded-lg border bg-card p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">New stock</span><span className="font-bold">{newStock} {stockUnit(product)}{newStock === 0 ? " · Sold Out" : ""}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Rate</span><span className="font-bold">{rupee(sr.value)} / {sr.unit.toLowerCase()}</span></div>
              <div className="flex justify-between text-primary"><span className="font-semibold">Sale value</span><span className="font-extrabold">{rupee(value)}</span></div>
            </div>
          )}
          {q > prev && <div className="text-xs font-semibold text-rose-600">You can't reduce more than current stock ({prev}).</div>}
          <Button data-testid="reduce-submit" onClick={submit} disabled={busy || invalid} className="w-full font-bold h-11">{busy ? "Saving…" : "Confirm Sale & Reduce Stock"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
